import { encryptToken } from "@/utils/crypto";

export interface EntraIdUser {
  id: string;
  email: string;
  name: string;
  department: string;
  role: string;
  avatarInitials: string;
  accessToken?: string;
  isCustom?: boolean;
}

const MOCK_USER: EntraIdUser = {
  id: "usr_mflopez",
  email: "mflopez@bidgroup.com",
  name: "María Fernanda López",
  department: "Finanzas",
  role: "Analista Financiero",
  avatarInitials: "MFL",
  accessToken: "mock_token_dev",
};

const USE_MOCK = import.meta.env.VITE_USE_MOCK === "true";

const CLIENT_ID = import.meta.env.VITE_ENTRA_ID_CLIENT_ID?.replace(/['"]/g, "").trim() || "";
const TENANT_ID = import.meta.env.VITE_ENTRA_ID_TENANT_ID?.replace(/['"]/g, "").trim() || "";
let AUTHORITY = import.meta.env.VITE_ENTRA_ID_AUTHORITY?.replace(/['"]/g, "").trim() || "";
let REDIRECT_URI = import.meta.env.VITE_ENTRA_ID_REDIRECT_URI?.replace(/['"]/g, "").trim() || window.location.origin;

// Asegurar que el redirectUri termine con una barra diagonal para coincidir exactamente con el registro de Azure Portal (evita AADSTS50011 y desajustes de comparación en MSAL)
if (!REDIRECT_URI.endsWith("/")) {
  REDIRECT_URI += "/";
}

if (TENANT_ID && (AUTHORITY.endsWith("/common") || AUTHORITY === "")) {
  AUTHORITY = `https://login.microsoftonline.com/${TENANT_ID}`;
}

let cachedUser: EntraIdUser | null = null;
let isInitialized = false;
let msalInstance: any = null;

async function getMsalInstance(): Promise<any> {
  if (msalInstance) return msalInstance;

  try {
    const { PublicClientApplication } = await import("@azure/msal-browser");
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: AUTHORITY,
        redirectUri: REDIRECT_URI,
      },
      cache: {
        cacheLocation: "localStorage",
      },
    });
    await msalInstance.initialize();

    // Procesar cualquier resultado de redirección al inicio (requerido por MSAL para flujos popup y redirect)
    const redirectResult = await msalInstance.handleRedirectPromise();

    if (redirectResult && redirectResult.account) {
      msalInstance.setActiveAccount(redirectResult.account);
      cachedUser = {
        id: redirectResult.account.localAccountId,
        email: redirectResult.account.username,
        name: redirectResult.account.name || "",
        department: (redirectResult.account.idTokenClaims as Record<string, unknown>)?.department as string || "",
        role: (redirectResult.account.idTokenClaims as Record<string, unknown>)?.jobTitle as string || "",
        avatarInitials: getInitials(redirectResult.account.name || ""),
      };
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
        const account = accounts[0];
        cachedUser = {
          id: account.localAccountId,
          email: account.username,
          name: account.name || "",
          department: (account.idTokenClaims as Record<string, unknown>)?.department as string || "",
          role: (account.idTokenClaims as Record<string, unknown>)?.jobTitle as string || "",
          avatarInitials: getInitials(account.name || ""),
        };
      }
    }
  } catch (error) {
    console.error("Failed to initialize MSAL instance:", error);
  }
  return msalInstance;
}

export function initEntraId(config: {
  clientId: string;
  authority: string;
  redirectUri: string;
  scopes: string[];
}): void {
  if (isInitialized) return;
  isInitialized = true;

  if (USE_MOCK) {
    cachedUser = MOCK_USER;
    return;
  }

  getMsalInstance().catch((err) => console.error("Async initEntraId failed:", err));
}

export async function signIn(): Promise<EntraIdUser> {
  if (USE_MOCK) {
    cachedUser = MOCK_USER;
    return MOCK_USER;
  }

  try {
    const msal = await getMsalInstance();
    if (!msal) throw new Error("MSAL no inicializado");

    // Guardar el destino antes de iniciar la redirección para el retorno automático
    if (!sessionStorage.getItem("post_login_redirect")) {
      sessionStorage.setItem("post_login_redirect", window.location.pathname + window.location.search);
    }

    await msal.loginRedirect({
      scopes: ["User.Read", "email", "profile", "openid"],
    });

    // Retornamos una promesa que nunca se resuelve porque la página se redirigirá
    return new Promise<EntraIdUser>(() => {});
  } catch (error) {
    console.error("Entra ID sign in failed:", error);
    throw error;
  }
}

export async function signOut(): Promise<void> {
  cachedUser = null;
  localStorage.removeItem("zammad_auth_token");
  localStorage.removeItem("zammad_auth_user");
  if (USE_MOCK) return;

  try {
    const msal = await getMsalInstance();
    if (msal) {
      await msal.logoutRedirect({
        postLogoutRedirectUri: REDIRECT_URI + "mis-tickets",
      });
    }
  } catch (error) {
    console.error("Entra ID sign out failed:", error);
  }
}

export async function getAccessToken(): Promise<string | null> {
  if (USE_MOCK) {
    return cachedUser?.accessToken || MOCK_USER.accessToken || null;
  }

  try {
    const msal = await getMsalInstance();
    if (!msal) return null;

    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) return null;

    const response = await msal.acquireTokenSilent({
      scopes: ["User.Read"],
      account: accounts[0],
    });

    return response.accessToken;
  } catch {
    return null;
  }
}

export function getCurrentUser(): EntraIdUser | null {
  const localUserStr = localStorage.getItem("zammad_auth_user");
  if (localUserStr) {
    try {
      return JSON.parse(localUserStr);
    } catch {
      // ignore
    }
  }
  if (USE_MOCK) {
    return cachedUser || MOCK_USER;
  }
  return cachedUser;
}

export async function getCurrentUserAsync(): Promise<EntraIdUser | null> {
  const localUserStr = localStorage.getItem("zammad_auth_user");
  if (localUserStr) {
    try {
      const user = JSON.parse(localUserStr);
      cachedUser = user;
      return user;
    } catch {
      // ignore
    }
  }

  if (USE_MOCK) {
    return cachedUser || MOCK_USER;
  }

  if (cachedUser) return cachedUser;

  try {
    const msal = await getMsalInstance();
    if (!msal) return null;

    const accounts = msal.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      msal.setActiveAccount(account);
      const user: EntraIdUser = {
        id: account.localAccountId,
        email: account.username,
        name: account.name || "",
        department: (account.idTokenClaims as Record<string, unknown>)?.department as string || "",
        role: (account.idTokenClaims as Record<string, unknown>)?.jobTitle as string || "",
        avatarInitials: getInitials(account.name || ""),
      };
      cachedUser = user;
      return user;
    }
  } catch (error) {
    console.error("Error getting current user from MSAL:", error);
  }

  return null;
}

export async function signInWithCredentials(username: string, password: string): Promise<EntraIdUser> {
  const token = `Basic ${btoa(unescape(encodeURIComponent(`${username}:${password}`)))}`;
  
  // Usar endpoint relativo si la URL de Zammad está vacía (proxy dev) u host absoluto
  const baseUrl = import.meta.env.VITE_ZAMMAD_BASE_URL || "";
  const res = await fetch(`${baseUrl}/api/v1/users/me`, {
    headers: {
      "Authorization": token,
      "Content-Type": "application/json",
    }
  });

  if (!res.ok) {
    throw new Error("Usuario o contraseña incorrectos");
  }

  const data = await res.json();
  const name = `${data.firstname || ""} ${data.lastname || ""}`.trim() || data.login || username;
  
  const user: EntraIdUser = {
    id: String(data.id),
    email: data.email || username,
    name: name,
    department: data.department || "General",
    role: data.roles?.includes("Admin") ? "Administrador" : "Usuario",
    avatarInitials: getInitials(name),
    accessToken: "zammad_local_session",
    isCustom: true,
  };

  const encrypted = encryptToken(token);
  localStorage.setItem("zammad_auth_token", encrypted);
  localStorage.setItem("zammad_auth_user", JSON.stringify(user));
  
  cachedUser = user;
  return user;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .join("")
    .toUpperCase()
    .slice(0, 3);
}